/**
 * Pure helpers + provider-agnostic readiness detection for the LiveChat
 * deep-link feature.
 */

export const LIVE_CHAT_DEEP_LINK_PARAM = 'openLiveChat';
export const LIVE_CHAT_DEEP_LINK_VALUE = 'true';

export const CHAT_READY_TIMEOUT_MS = 30000;
export const POLL_INTERVAL_MS = 250;
export const DEEP_LINK_SESSION_KEY = 'livechat-deep-link-handled';

export const classifyLiveChatDeepLink = search => {
  try {
    const params = new URLSearchParams(search);

    return params.get(LIVE_CHAT_DEEP_LINK_PARAM) === LIVE_CHAT_DEEP_LINK_VALUE;
  } catch {
    return false;
  }
};

export const removeLiveChatParam = search => {
  try {
    const params = new URLSearchParams(search);
    params.delete(LIVE_CHAT_DEEP_LINK_PARAM);

    return params.toString();
  } catch {
    return '';
  }
};

const READINESS_PREDICATES = {
  Infobip: () => typeof window.liveChat === 'function',
  LiveChat: () => !!window.LiveChatWidget,
  Octadesk: () => !!window.octadesk?.chat?.showApp,
  'Respond.io': () => !!window.$respond?.do,
  'Tawk.to': () => !!(window.Tawk_API && typeof window.Tawk_API.maximize === 'function'),
  Zendesk: () => typeof window.zE === 'function',
  ZendeskWebWidget: () => typeof window.zE === 'function',
};

export const isProviderReady = chatType => {
  try {
    const predicate = READINESS_PREDICATES[chatType];

    return predicate ? predicate() : true;
  } catch {
    return false;
  }
};

export const waitForChatReady = (chatType, { onReady, onTimeout, timeoutMs }) => {
  const isLiveChat = chatType === 'LiveChat';

  let settled = false;
  let interval = null;
  let deadline = null;
  let removeReadyListener = null;
  let readyListenerAttached = false;

  const cleanup = () => {
    if (interval !== null) {
      clearInterval(interval);
      interval = null;
    }

    if (deadline !== null) {
      clearTimeout(deadline);
      deadline = null;
    }

    if (removeReadyListener) {
      removeReadyListener();
      removeReadyListener = null;
    }
  };

  const settleReady = () => {
    if (settled) {
      return;
    }

    settled = true;
    cleanup();

    if (typeof onReady === 'function') {
      onReady();
    }
  };

  const settleTimeout = () => {
    if (settled) {
      return;
    }

    settled = true;
    cleanup();

    if (typeof onTimeout === 'function') {
      onTimeout();
    }
  };

  const attachReadyListener = () => {
    if (readyListenerAttached) {
      return true;
    }

    try {
      if (window.LiveChatWidget && typeof window.LiveChatWidget.on === 'function') {
        const handler = () => settleReady();
        window.LiveChatWidget.on('ready', handler);
        readyListenerAttached = true;

        removeReadyListener = () => {
          try {
            if (window.LiveChatWidget && typeof window.LiveChatWidget.off === 'function') {
              window.LiveChatWidget.off('ready', handler);
            }
          } catch {
            // Ignore listener teardown errors.
          }
        };

        return true;
      }
    } catch {
      // Ignore listener registration errors; the timeout still bounds us.
    }

    return false;
  };

  if (isLiveChat) {
    attachReadyListener();

    if (!readyListenerAttached) {
      interval = setInterval(() => {
        if (attachReadyListener()) {
          clearInterval(interval);
          interval = null;
        }
      }, POLL_INTERVAL_MS);
    }

    deadline = setTimeout(settleTimeout, timeoutMs);

    return cleanup;
  }

  if (isProviderReady(chatType)) {
    settleReady();

    return cleanup;
  }

  interval = setInterval(() => {
    if (isProviderReady(chatType)) {
      settleReady();
    }
  }, POLL_INTERVAL_MS);

  deadline = setTimeout(settleTimeout, timeoutMs);

  return cleanup;
};
